import { Field, FieldGrid, FormBlock, Input, MoneyInput, Textarea } from '@westhetics/ui'
import type { Procedure } from './api'
import { CatalogPicker } from './catalog-picker'
import { CATEGORY_LABELS, documentsLabel } from './labels'
import { PaymentTermsFields } from './payment-terms-fields'
import {
  PROCEDURE_NAME_LIMIT,
  withChosenItems,
  type ProcedureFormErrors,
  type ProcedureFormState,
} from './procedure-form-state'

/**
 * Os blocos do formulário: o que inclui, como aparece, valor e pagamento e os documentos que
 * acompanham. Quem usa dá o `<form>`, o rodapé e o estado.
 */
export function ProcedureFormBlocks({
  form,
  errors,
  onChange,
  procedure,
}: {
  form: ProcedureFormState
  errors: ProcedureFormErrors
  onChange: (next: ProcedureFormState) => void
  /** O procedimento salvo, sem alteração na tela: a API já diz os documentos pelo nome. */
  procedure?: Procedure
}) {
  const update = (patch: Partial<ProcedureFormState>) => onChange({ ...form, ...patch })
  return (
    <>
      <FormBlock
        title="O que inclui"
        note="Escolha um ou mais procedimentos da lista da Westhetics. Cada um traz os termos que a paciente assina; o procedimento da clínica junta todos."
      >
        <div className="flex flex-col gap-4">
          <div>
            <CatalogPicker
              multiple
              value={form.items.map((item) => item.id)}
              onChange={(_ids, options) => onChange(withChosenItems(form, options))}
            />
            {errors.items ? (
              <p
                role="alert"
                className="font-body text-eyebrow text-plum mt-[6px] leading-[1.5] font-light tracking-normal"
              >
                {errors.items}
              </p>
            ) : null}
          </div>
          {form.items.length > 0 ? (
            <FieldGrid>
              {form.items.map((item) => (
                <Field key={item.id} label={`Aplicações · ${item.name}`}>
                  <Input
                    inputMode="numeric"
                    value={String(item.quantity)}
                    onChange={(event) => {
                      const quantity = Math.min(
                        99,
                        Math.max(1, Number(event.target.value.replace(/\D/g, '')) || 1),
                      )
                      update({
                        items: form.items.map((current) =>
                          current.id === item.id ? { ...current, quantity } : current,
                        ),
                      })
                    }}
                  />
                </Field>
              ))}
            </FieldGrid>
          ) : null}
        </div>
      </FormBlock>

      <FormBlock
        title="Como aparece"
        note="É o nome que a paciente lê no contrato e no plano de tratamento."
      >
        <div className="flex flex-col gap-4">
          <Field
            label="Nome"
            meta={`${form.name.trim().length}/${PROCEDURE_NAME_LIMIT}`}
            error={errors.name}
            hint="Vem dos procedimentos escolhidos; troque pelo nome que a clínica usa. Sem vírgulas — elas separam procedimentos no documento gerado."
          >
            <Input
              value={form.name}
              maxLength={120}
              onChange={(event) =>
                update({ name: event.target.value, nameTouched: event.target.value.trim() !== '' })
              }
            />
          </Field>
          <Field
            label="Descrição"
            optional
            hint="Regras, o que inclui, condições — o que antes era forçado dentro do nome."
          >
            <Textarea
              value={form.description}
              maxLength={500}
              onChange={(event) => update({ description: event.target.value })}
            />
          </Field>
        </div>
      </FormBlock>

      <FormBlock
        title="Valor e pagamento"
        note="A frase do contrato é gerada, não digitada, e fica igual em todo documento."
      >
        <div className="flex flex-col gap-4">
          <FieldGrid>
            <Field label="Valor" error={errors.amountCents}>
              <MoneyInput
                value={form.amountCents}
                onChange={(amountCents) => update({ amountCents })}
              />
            </Field>
            <Field
              label="Vigência em meses"
              optional
              error={errors.validityMonths}
              hint="Contada da assinatura. Deixe vazio quando tudo é feito no mesmo atendimento."
            >
              <Input
                inputMode="numeric"
                placeholder="12"
                value={form.validityMonths}
                onChange={(event) =>
                  update({ validityMonths: event.target.value.replace(/\D/g, '') })
                }
              />
            </Field>
          </FieldGrid>
          <PaymentTermsFields
            value={form.paymentTerms}
            onChange={(paymentTerms) => update({ paymentTerms })}
            discountText={form.discountText}
            onDiscountText={(discountText) => update({ discountText })}
            errors={{ methods: errors.methods, upfrontDiscount: errors.upfrontDiscount }}
          />
        </div>
      </FormBlock>

      <FormBlock
        title="Documentos que acompanham"
        note="Vêm da lista da Westhetics e entram em todo atendimento com este procedimento. Nada é escolhido aqui."
      >
        <DocumentsSummary form={form} procedure={procedure} />
      </FormBlock>
    </>
  )
}

/** Só leitura: o que a base procedimento × contrato liga aos itens escolhidos (RN-ADM-73). */
function DocumentsSummary({
  form,
  procedure,
}: {
  form: ProcedureFormState
  procedure?: Procedure
}) {
  const rowClass = 'font-body text-body text-ink leading-[1.8] font-light'
  if (form.items.length === 0) {
    return (
      <p className="font-body text-body text-secondary font-light">
        Escolha os procedimentos para ver os termos que chegam com eles.
      </p>
    )
  }
  // Sem alteração na composição, a API já disse os documentos pelo nome.
  if (procedure && procedure.documents.length > 0) {
    return (
      <ul className="border-hairline bg-pampas border px-4 py-3">
        {procedure.documents.map((document) => (
          <li key={document.templateId} className={rowClass}>
            {document.name}
            <span className="text-secondary">
              {' '}
              · {CATEGORY_LABELS[document.category] ?? document.category} ·{' '}
              {document.isRequired ? 'obrigatório' : 'sugerido'}
            </span>
          </li>
        ))}
      </ul>
    )
  }
  return (
    <ul className="border-hairline bg-pampas border px-4 py-3">
      {form.items.map((item) => (
        <li key={item.id} className={rowClass}>
          {item.name}
          {item.hasConsentForm === false ? (
            <span className="text-plum">
              {' '}
              · a Westhetics ainda não publicou o termo deste procedimento
            </span>
          ) : (
            <span className="text-secondary">
              {' '}
              ·{' '}
              {item.documentCount === null
                ? 'os termos da lista da Westhetics'
                : documentsLabel(item.documentCount)}
            </span>
          )}
        </li>
      ))}
    </ul>
  )
}
